import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGetMany } from 'react-admin'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { discussionCsApi, DiscussionCsApiError } from './chapterDiscussions.api'
import {
  buildContentActionRequest,
  isDestructiveContentAction,
} from './chapterDiscussions.model'
import { DiscussionActionDialog } from './DiscussionActionDialog'
import { DiscussionUserEnforcementPanel } from './DiscussionUserEnforcementPanel'

import type {
  ChapterCommentReport,
  DiscussionCommentDetail,
  DiscussionContentActionRequest,
  DiscussionCsCommentIdentity,
  DiscussionReportActionRequest,
  ModerationAction,
  SerializedTimestamp,
} from './chapterDiscussions.types'

export interface DiscussionModerationShowProps {
  identity: DiscussionCsCommentIdentity
}

export const CONTENT_ACTION_BUTTONS = [
  { action: 'approve', label: 'Approve' },
  { action: 'hide', label: 'Reject' },
] as const

export const APPROVE_BUTTON_SX = {
  '--Button-containedBg': '#58A700',
  '--Button-containedBgGradient': 'none',
  '--Button-containedStroke': 'none',
  bgcolor: '#58A700',
  backgroundImage: 'none',
  borderRadius: 1,
  px: 2,
  py: 1,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.5,
  textTransform: 'none',
  '&:hover': {
    '--Button-containedBg': '#4D9200',
    bgcolor: '#4D9200',
    backgroundImage: 'none',
  },
}

export const REJECT_BUTTON_SX = {
  '--Button-containedBg': '#F96262',
  '--Button-containedBgGradient': 'none',
  '--Button-containedStroke': 'none',
  bgcolor: '#F96262',
  backgroundImage: 'none',
  borderRadius: 1,
  px: 2,
  py: 1,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.5,
  textTransform: 'none',
  '&:hover': {
    '--Button-containedBg': '#E55656',
    bgcolor: '#E55656',
    backgroundImage: 'none',
  },
}

export function ModerationDetailHeader({
  pending,
  onAction,
}: {
  pending: boolean
  onAction: (action: DiscussionContentActionRequest['action']) => void
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: 2 }}>
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: 24, md: 28 },
          fontWeight: 700,
          lineHeight: 1.5,
        }}>
        Moderation Detail
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckIcon />}
          disabled={pending}
          sx={APPROVE_BUTTON_SX}
          onClick={() => onAction('approve')}>
          Approve
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<CloseIcon />}
          disabled={pending}
          sx={REJECT_BUTTON_SX}
          onClick={() => onAction('hide')}>
          Reject
        </Button>
      </Stack>
    </Stack>
  )
}

function timestampText(value: SerializedTimestamp | string | undefined) {
  if (!value) return '—'
  if (typeof value === 'string') return new Date(value).toLocaleString()
  const seconds = value._seconds ?? value.seconds
  return seconds === undefined ? '—' : new Date(seconds * 1000).toLocaleString()
}

const DETAIL_CARD_SX = {
  p: 2,
  borderColor: '#DCDFE4',
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
}

const DETAIL_LABEL_SX = {
  color: '#5F708F',
  fontSize: 16,
  lineHeight: 1.5,
}

function DetailStatusRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography sx={DETAIL_LABEL_SX}>{label}:</Typography>
      <Chip
        label={value}
        size="small"
        sx={{
          height: 24,
          bgcolor: '#EBEBEB',
          color: '#5F708F',
          fontSize: 14,
          lineHeight: 1.5,
          '& .MuiChip-label': { px: 1 },
        }}
      />
    </Stack>
  )
}

export function DetailSummarySection({
  detail,
}: {
  detail: Pick<
    DiscussionCommentDetail,
    'comment' | 'bookTitle' | 'chapterTitle'
  >
}) {
  const { comment } = detail

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 5,
      }}>
      <Paper variant="outlined" sx={DETAIL_CARD_SX}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.5 }}>
          Comment / Reply
        </Typography>
        <Typography
          sx={{
            color: '#1A3154',
            fontSize: 20,
            lineHeight: 1.5,
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
          }}>
          {comment.body}
        </Typography>
        <Divider />
        <Stack spacing={0.5}>
          <DetailStatusRow
            label="Moderation"
            value={comment.moderationStatus}
          />
          <DetailStatusRow
            label="Visibility"
            value={comment.visibilityStatus}
          />
          <DetailStatusRow
            label="Revision"
            value={`revision ${comment.contentRevision}`}
          />
          <Typography sx={DETAIL_LABEL_SX}>
            Last updated: {timestampText(comment.updatedAt)}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={DETAIL_CARD_SX}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.5 }}>
          Book Info
        </Typography>
        <Stack spacing={0.5} sx={{ overflowWrap: 'anywhere' }}>
          <Typography sx={DETAIL_LABEL_SX}>
            Book title: {detail.bookTitle ?? `Book #${comment.bookId}`}
          </Typography>
          <Typography sx={DETAIL_LABEL_SX}>
            Chapter: {detail.chapterTitle ?? `Chapter #${comment.chapterId}`}
          </Typography>
          <Typography sx={DETAIL_LABEL_SX}>
            Book ID: {comment.bookId}
          </Typography>
          <Typography sx={DETAIL_LABEL_SX}>
            Chapter ID: {comment.chapterId}
          </Typography>
          <Typography sx={DETAIL_LABEL_SX}>
            Comment ID: {comment.commentId}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  )
}

const MODERATION_HISTORY_COLUMN_WIDTHS = [
  '15%',
  '7%',
  '9%',
  '14%',
  '21%',
  '20%',
  '14%',
]

function entitlementText(
  eligible: boolean | undefined,
  reason: string | undefined,
) {
  if (eligible === undefined) return '—'
  const status = eligible ? 'eligible' : 'ineligible'
  return reason ? `${status} · ${reason}` : status
}

export function EvidenceSection({
  detail,
}: {
  detail: Pick<DiscussionCommentDetail, 'moderationRuns'>
}) {
  const runs = detail.moderationRuns ?? []
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
      <Typography
        sx={{
          mb: 2,
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 1.5,
        }}>
        AI Moderation History
      </Typography>
      {runs.length === 0 ? (
        <Typography color="text.secondary">No moderation runs.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              width: '100%',
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                borderBottom: '1px solid rgba(26, 49, 84, 0.1)',
                color: 'text.primary',
                overflowWrap: 'anywhere',
                pl: 0,
                pr: 3,
              },
              '& .MuiTableCell-root:last-of-type': { pr: 0 },
            }}>
            <colgroup>
              {MODERATION_HISTORY_COLUMN_WIDTHS.map((width, index) => (
                <col key={`${width}:${index}`} style={{ width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ height: 56 }}>
                {[
                  'Time',
                  'Revision',
                  'Decision',
                  'Reason',
                  'Comment / Reply',
                  'Policy / model',
                  'Entitlement',
                ].map(label => (
                  <TableCell
                    key={label}
                    sx={{
                      bgcolor: 'background.paper',
                      py: 2,
                      fontSize: 15,
                      fontWeight: 700,
                      lineHeight: '20px',
                    }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run, index) => (
                <TableRow
                  key={run.runId ?? `${run.contentRevision}:${index}`}
                  sx={{ height: 64 }}>
                  <TableCell sx={{ py: 1.5, fontSize: 14, lineHeight: '20px' }}>
                    {timestampText(run.completedAt)}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, lineHeight: '20px' }}>
                    {run.contentRevision}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, lineHeight: '20px' }}>
                    {run.decision ?? '—'}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, lineHeight: '20px' }}>
                    {run.reasonCodes?.join(', ') || '--'}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontSize: 14,
                      lineHeight: '20px',
                      whiteSpace: 'pre-wrap',
                    }}>
                    {run.bodySnapshot ?? '—'}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, lineHeight: '20px' }}>
                    {run.policyVersion ?? '—'}
                    {run.provider && (
                      <>
                        <br />
                        {run.provider} / {run.model ?? '—'}
                      </>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, lineHeight: '20px' }}>
                    {entitlementText(
                      run.entitlementEligible,
                      run.entitlementReason,
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  )
}

const CS_MODERATION_HISTORY_COLUMNS = [
  { label: 'Time', width: '17%' },
  { label: 'Revision', width: '10%' },
  { label: 'Decision', width: '13%' },
  { label: 'Action by', width: '16%' },
  { label: 'Reason', width: '18%' },
  { label: 'Comment / Reply', width: '26%' },
]

const CS_ACTION_DECISIONS: Record<ModerationAction['action'], string> = {
  approve: 'approved',
  hide: 'rejected',
  restore: 'restored',
  reevaluate: 'reevaluated',
  delete: 'deleted',
}

function moderationActionBody(
  detail: Pick<
    DiscussionCommentDetail,
    'comment' | 'moderationRuns' | 'moderationActions'
  >,
  contentRevision: number,
) {
  const run = [...(detail.moderationRuns ?? [])]
    .reverse()
    .find(item => item.contentRevision === contentRevision)
  if (run?.bodySnapshot) return run.bodySnapshot
  return detail.comment.contentRevision === contentRevision
    ? detail.comment.body
    : '—'
}

export function CsModerationHistorySection({
  detail,
  actorNamesById,
}: {
  detail: Pick<
    DiscussionCommentDetail,
    'comment' | 'moderationRuns' | 'moderationActions'
  >
  actorNamesById: ReadonlyMap<number, string>
}) {
  const actions = detail.moderationActions ?? []
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
      <Typography
        sx={{
          mb: 2,
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 1.5,
        }}>
        CS Moderation History
      </Typography>
      {actions.length === 0 ? (
        <Typography color="text.secondary">No CS actions.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              width: '100%',
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                borderBottom: '1px solid rgba(26, 49, 84, 0.1)',
                color: 'text.primary',
                overflowWrap: 'anywhere',
                pl: 0,
                pr: 3,
              },
              '& .MuiTableCell-root:last-of-type': { pr: 0 },
            }}>
            <colgroup>
              {CS_MODERATION_HISTORY_COLUMNS.map(column => (
                <col key={column.label} style={{ width: column.width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ height: 56 }}>
                {CS_MODERATION_HISTORY_COLUMNS.map(column => (
                  <TableCell
                    key={column.label}
                    sx={{
                      bgcolor: 'background.paper',
                      py: 2,
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: 1.5,
                    }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {actions.map((action, index) => (
                <TableRow key={action.actionId ?? index}>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    {timestampText(action.createdAt)}
                  </TableCell>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    {action.contentRevision}
                  </TableCell>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    {CS_ACTION_DECISIONS[action.action]}
                  </TableCell>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    <Link
                      href={`#/user-details/${action.actorId}/show`}
                      underline="hover"
                      sx={{ color: '#F47D42' }}>
                      {actorNamesById.get(action.actorId) ??
                        `User ${action.actorId}`}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                    {action.reason || '--'}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 2,
                      fontSize: 16,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}>
                    {moderationActionBody(detail, action.contentRevision)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  )
}

interface DiscussionActorRecord {
  id: number
  name?: string
  username?: string
}

const USER_REPORT_COLUMNS = [
  { label: 'Time', width: '17%' },
  { label: 'Revision', width: '10%' },
  { label: 'Reporter', width: '14%' },
  { label: 'Reason', width: '17%' },
  { label: 'Note', width: '18%' },
  { label: 'Comment / Reply', width: '24%' },
]

export function UserReportSection({
  detail,
  pending = false,
  onReportAction,
}: {
  detail: Pick<DiscussionCommentDetail, 'reports' | 'reportActions'>
  pending?: boolean
  onReportAction?: (
    report: ChapterCommentReport,
    action: DiscussionReportActionRequest['action'],
  ) => void
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
      <Typography
        sx={{
          mb: 2,
          fontSize: 20,
          fontWeight: 700,
          lineHeight: 1.5,
        }}>
        User Report
      </Typography>
      {detail.reports.length === 0 ? (
        <Typography color="text.secondary">No reports.</Typography>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid rgba(26, 49, 84, 0.1)',
                  color: 'text.primary',
                  overflowWrap: 'anywhere',
                  pl: 0,
                  pr: 3,
                },
                '& .MuiTableCell-root:last-of-type': { pr: 0 },
              }}>
              <colgroup>
                {USER_REPORT_COLUMNS.map(column => (
                  <col key={column.label} style={{ width: column.width }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow sx={{ height: 56 }}>
                  {USER_REPORT_COLUMNS.map(column => (
                    <TableCell
                      key={column.label}
                      sx={{
                        bgcolor: 'background.paper',
                        py: 2,
                        fontSize: 16,
                        fontWeight: 700,
                        lineHeight: 1.5,
                      }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.reports.map(report => (
                  <TableRow key={report.reportId}>
                    <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                      {timestampText(report.createdAt)}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                      {report.reportedContentRevision}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                      <Link
                        href={`#/user-details/${report.reporterUserId}/show`}
                        underline="hover"
                        sx={{ color: '#F47D42' }}>
                        {report.reporterUserId}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                      {report.reason}
                    </TableCell>
                    <TableCell sx={{ py: 2, fontSize: 16, lineHeight: 1.5 }}>
                      {report.details || '--'}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        fontSize: 16,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}>
                      {report.reportedBodySnapshot ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {detail.reports.map(report => (
            <Box key={`${report.reportId}:actions`} sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={report.status} />
                {report.status === 'open' && onReportAction && (
                  <>
                    <Button
                      size="small"
                      disabled={pending}
                      onClick={() => onReportAction(report, 'resolve')}>
                      Resolve
                    </Button>
                    <Button
                      size="small"
                      disabled={pending}
                      onClick={() => onReportAction(report, 'dismiss')}>
                      Dismiss
                    </Button>
                  </>
                )}
              </Stack>
              {(detail.reportActions?.[report.reportId] ?? []).map(
                (action, index) => (
                  <Typography
                    key={action.actionId ?? index}
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 1 }}>
                    {action.action} by {action.actorId}: {action.reason} ·{' '}
                    {timestampText(action.createdAt)}
                  </Typography>
                ),
              )}
            </Box>
          ))}
        </>
      )}
    </Paper>
  )
}

export function DiscussionModerationShow({
  identity,
}: DiscussionModerationShowProps) {
  const [detail, setDetail] = useState<DiscussionCommentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [staleState, setStaleState] = useState<Record<string, unknown> | null>(
    null,
  )
  const [contentAction, setContentAction] = useState<
    DiscussionContentActionRequest['action'] | null
  >(null)
  const [reportAction, setReportAction] = useState<{
    report: ChapterCommentReport
    action: DiscussionReportActionRequest['action']
  } | null>(null)
  const [pending, setPending] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await discussionCsApi.getComment(identity)
      setDetail(response)
      setError(null)
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Unable to load discussion detail',
      )
    } finally {
      setLoading(false)
    }
  }, [identity])

  useEffect(() => {
    load()
    const refresh = () => load()
    window.addEventListener('discussion-detail-refresh', refresh)
    return () =>
      window.removeEventListener('discussion-detail-refresh', refresh)
  }, [load])

  const policyVersion = useMemo(() => {
    const runs = detail?.moderationRuns ?? []
    return (
      [...runs].reverse().find(run => run.policyVersion)?.policyVersion ??
      detail?.latestModerationRun?.policyVersion ??
      'vi-discussion-v1'
    )
  }, [detail])

  const actionActorIds = useMemo(
    () =>
      Array.from(
        new Set(
          [
            detail?.comment.authorId,
            ...(detail?.moderationActions ?? []).map(action => action.actorId),
            ...(detail?.enforcementActions ?? []).map(action => action.actorId),
          ].filter((id): id is number => id !== undefined),
        ),
      ),
    [detail],
  )
  const { data: actionActors = [] } = useGetMany<DiscussionActorRecord>(
    'users',
    { ids: actionActorIds },
    { enabled: actionActorIds.length > 0 },
  )
  const actionActorNamesById = useMemo(
    () =>
      new Map(
        actionActors.map(actor => [
          Number(actor.id),
          actor.name?.trim() || actor.username?.trim() || `User ${actor.id}`,
        ]),
      ),
    [actionActors],
  )

  const contentActionLabel = CONTENT_ACTION_BUTTONS.find(
    button => button.action === contentAction,
  )?.label

  const handleApiError = async (requestError: unknown) => {
    const apiError = requestError as DiscussionCsApiError
    setError(apiError.message)
    if (apiError.status === 409 && apiError.errorCode === 'stale_state') {
      setStaleState(apiError.current ?? {})
      setContentAction(null)
      setReportAction(null)
      await load()
    }
  }

  const submitContentAction = async (reason: string) => {
    if (!detail || !contentAction) return
    setPending(true)
    try {
      await discussionCsApi.createContentAction(
        identity,
        buildContentActionRequest({
          action: contentAction,
          reason,
          policyVersion,
          comment: detail.comment,
        }),
      )
      setContentAction(null)
      await load()
    } catch (requestError) {
      await handleApiError(requestError)
    } finally {
      setPending(false)
    }
  }

  const submitReportAction = async (reason: string) => {
    if (!reportAction) return
    setPending(true)
    try {
      await discussionCsApi.createReportAction(
        identity,
        reportAction.report.reportId,
        {
          action: reportAction.action,
          reason,
          expectedStatus: reportAction.report.status,
        },
      )
      setReportAction(null)
      await load()
    } catch (requestError) {
      await handleApiError(requestError)
    } finally {
      setPending(false)
    }
  }

  if (loading && !detail) {
    return (
      <Stack alignItems="center" sx={{ p: 6 }}>
        <CircularProgress />
      </Stack>
    )
  }
  if (!detail) {
    return (
      <Alert severity="error">
        {error ?? 'Discussion comment could not be loaded.'}
      </Alert>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <ModerationDetailHeader pending={pending} onAction={setContentAction} />
      {staleState && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setStaleState(null)}>
          The discussion changed while the action was open. The draft was
          discarded and current data was reloaded.
          {Object.keys(staleState).length > 0 &&
            ` Current state: ${JSON.stringify(staleState)}`}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <DetailSummarySection detail={detail} />

        {detail.parentComment && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Reviewed parent context</Typography>
            <Typography variant="body2" color="text.secondary">
              Parent {detail.parentComment.commentId} · reviewed revision{' '}
              {detail.comment.moderatedParentRevision ?? '—'}
            </Typography>
            <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {detail.parentEvidence?.bodySnapshot ?? detail.parentComment.body}
            </Typography>
          </Paper>
        )}

        <EvidenceSection detail={detail} />

        <CsModerationHistorySection
          detail={detail}
          actorNamesById={actionActorNamesById}
        />

        <UserReportSection
          detail={detail}
          pending={pending}
          onReportAction={(report, action) =>
            setReportAction({ report, action })
          }
        />

        <DiscussionUserEnforcementPanel
          userId={detail.comment.authorId}
          userName={
            actionActorNamesById.get(detail.comment.authorId) ??
            detail.authorName ??
            `User ${detail.comment.authorId}`
          }
          enforcement={detail.enforcement}
          enforcementActions={detail.enforcementActions}
          actorNamesById={actionActorNamesById}
        />

        {(detail.bulkDeleteJobs?.length ?? 0) > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Delete-all history</Typography>
            {detail.bulkDeleteJobs?.map(job => (
              <Typography key={job.jobId} variant="body2" sx={{ mt: 1 }}>
                {job.jobId}: {job.state} · scanned {job.scannedCount} · deleted{' '}
                {job.deletedCount} · failed {job.failedCount}
              </Typography>
            ))}
          </Paper>
        )}
      </Stack>

      <DiscussionActionDialog
        open={contentAction !== null}
        title={`${contentActionLabel ?? ''} comment`}
        description={`Apply ${contentActionLabel ?? ''} to the exact revision currently displayed.`}
        confirmLabel={contentActionLabel ?? 'Confirm'}
        destructive={
          contentAction ? isDestructiveContentAction(contentAction) : false
        }
        pending={pending}
        error={error}
        onClose={() => setContentAction(null)}
        onConfirm={submitContentAction}
      />
      <DiscussionActionDialog
        open={reportAction !== null}
        title={`${reportAction?.action ?? ''} report`}
        description="This changes only the report workflow state. It does not change comment visibility."
        confirmLabel={reportAction?.action ?? 'Confirm'}
        pending={pending}
        error={error}
        onClose={() => setReportAction(null)}
        onConfirm={submitReportAction}
      />
    </Box>
  )
}
